import { useReducer } from 'react'
import { useMutation } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { LoginForm } from '@/pages/login/LoginForm'
import { TOTPForm } from '@/pages/login/TOTPForm'
import { initialLoginState, loginReducer } from '@/pages/login/loginMachine'
import { loginRequest, verifyRequest } from '@/lib/authApi'
import { is2FARequired } from '@/types/auth'
import { useAuthStore } from '@/stores/auth'

const ERRORS = {
  rateLimited: 'Zu viele Versuche. Bitte eine Minute warten.',
  invalidCredentials: 'Benutzername oder Passwort falsch.',
  invalid2fa: 'Code ungültig oder Anmeldung abgelaufen.',
  network: 'Server nicht erreichbar. Bitte später erneut versuchen.',
  unknown: 'Unerwarteter Fehler. Bitte erneut versuchen.',
} as const

function mapError(error: unknown, phase: 'credentials' | 'totp'): string {
  if (!isAxiosError(error)) return ERRORS.unknown
  if (!error.response) return ERRORS.network
  switch (error.response.status) {
    case 429:
      return ERRORS.rateLimited
    case 401:
      return phase === 'credentials' ? ERRORS.invalidCredentials : ERRORS.invalid2fa
    case 422:
      return ERRORS.invalid2fa
    default:
      return ERRORS.unknown
  }
}

export function LoginPage() {
  const [state, dispatch] = useReducer(loginReducer, initialLoginState)
  const setAccessToken = useAuthStore((s) => s.setAccessToken)

  const loginMutation = useMutation({
    mutationFn: loginRequest,
    onMutate: () => dispatch({ type: 'SUBMITTING' }),
    onSuccess: (res) => {
      if (is2FARequired(res)) {
        dispatch({ type: 'CHALLENGE_RECEIVED', challengeToken: res.challenge_token })
      } else {
        setAccessToken(res.access_token)
      }
    },
    onError: (err) => dispatch({ type: 'ERROR', message: mapError(err, 'credentials') }),
  })

  const verifyMutation = useMutation({
    mutationFn: verifyRequest,
    onMutate: () => dispatch({ type: 'SUBMITTING' }),
    onSuccess: (res) => setAccessToken(res.access_token),
    onError: (err) => dispatch({ type: 'ERROR', message: mapError(err, 'totp') }),
  })

  const phaseLabel = state.phase === 'credentials' ? 'Login' : '2-Faktor-Authentifizierung'

  return (
    <main className="flex min-h-screen flex-col bg-void text-ink">
      <div className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="fade-in w-full max-w-sm border border-hairline bg-panel">
          <header className="hairline-b px-8 pt-7 pb-6">
            <p className="tracked-caps text-2xl text-ink">Sextant</p>
            <p className="mt-2 font-mono text-[11px] text-muted">v0.1.0 · Portfolio</p>
          </header>

          <div className="px-8 pt-7 pb-8">
            <p className="tracked-caps mb-7 text-[10px] text-muted">{phaseLabel}</p>

            {state.phase === 'credentials' ? (
              <LoginForm
                onSubmit={(creds) => loginMutation.mutate(creds)}
                isPending={loginMutation.isPending}
                error={state.error}
              />
            ) : (
              <>
                <TOTPForm
                  onSubmit={(code) =>
                    verifyMutation.mutate({
                      challenge_token: state.challengeToken,
                      ...code,
                    })
                  }
                  isPending={verifyMutation.isPending}
                  error={state.error}
                />
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'RESET' })}
                  disabled={verifyMutation.isPending}
                  className="mt-6 text-xs text-muted transition-colors hover:text-bernstein disabled:opacity-60"
                >
                  ← Neu anmelden
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <footer className="px-6 py-4 text-center">
        <p className="tracked-caps font-mono text-[10px] text-faint">
          Geschützter Zugang · 2FA erforderlich
        </p>
      </footer>
    </main>
  )
}
